import { Eye, Palette, Pencil, Share2, ShieldCheck, Star } from "lucide-react";
import { formatRelativeTime } from "@/pages/apps/microApps/relativeTime";
import type { BrandRole, BrandSummary } from "@/types";

function RoleBadge({ role }: Readonly<{ role: BrandRole | null }>) {
  if (role === "admin") {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-brand-primary">
        <ShieldCheck className="w-3 h-3" /> Admin
      </span>
    );
  }
  if (role === "editor") {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-blue-400">
        <Pencil className="w-3 h-3" /> Editor
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs text-content-tertiary">
      <Eye className="w-3 h-3" /> Viewer
    </span>
  );
}

/** A single brand card in the gallery grid. */
export function BrandCard({
  brand,
  onOpen,
  onShare,
}: Readonly<{ brand: BrandSummary; onOpen: (uuid: string) => void; onShare?: (uuid: string) => void }>) {
  return (
    <div className="rounded-xl border border-border-primary bg-surface-primary hover:border-brand-primary/50 transition group relative">
      {/* Full-card open action — a real <button> for accessibility; the share
          button sits above it (z-10) so both remain independently operable. */}
      <button
        type="button"
        onClick={() => onOpen(brand.uuid)}
        aria-label={`Open ${brand.name}`}
        className="absolute inset-0 rounded-xl cursor-pointer"
      />
      {onShare && brand.currentUserRole === "admin" && (
        <button
          type="button"
          onClick={() => onShare(brand.uuid)}
          aria-label={`Share ${brand.name}`}
          title="Manage access"
          className="absolute top-3 right-3 z-10 p-1.5 rounded-lg text-content-tertiary hover:bg-interactive-hover hover:text-content-primary transition opacity-0 group-hover:opacity-100 focus:opacity-100"
        >
          <Share2 className="h-4 w-4" />
        </button>
      )}
      <div className="relative pointer-events-none text-left p-4">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 shrink-0 rounded-lg bg-brand-primary/10 text-brand-primary flex items-center justify-center">
            <Palette className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-content-primary truncate group-hover:text-brand-primary transition">
                {brand.name}
              </h3>
              {brand.isDefault && (
                <span className="inline-flex items-center gap-1 shrink-0 rounded-full bg-brand-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-brand-primary">
                  <Star className="w-2.5 h-2.5" /> Default
                </span>
              )}
            </div>
            {brand.description && (
              <p className="text-sm text-content-tertiary line-clamp-2 mt-0.5">{brand.description}</p>
            )}
            <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-2">
              <RoleBadge role={brand.currentUserRole} />
              {brand.audience && (
                <span className="text-xs text-content-quaternary rounded bg-surface-secondary px-1.5 py-0.5">
                  {brand.audience}
                </span>
              )}
              <span className="text-xs text-content-quaternary">Updated {formatRelativeTime(brand.updatedAt)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
